import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { FlexColumnContainer, useWindowDimensions } from "@trailhead/core-ui";
import {
  Button as AsurionButton,
  Text,
  breakpoints,
  Icon,
  Size,
  colors,
} from "@trailhead/asurion-ui";
import { trackEvent } from "@trailhead/analytics";
import { AEClient, TwilioClient } from "@trailhead/chat";
import { useSanitizedTranslation } from "@trailhead/i18n";
// import { ChatBannerAnimationV4, LottiePlayer } from "@trailhead/lottie";

import PhoneWithIcons from "../assets/Experts.png";
import FeedbackStars from "../assets/FeedbackStars.png";


const Container = styled.div`
  width: 100vw;
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  background: linear-gradient(180deg, #f7f7f7 0%, rgba(255, 255, 255, 0) 27.64%),
    #ffffff;
  margin: 0 -100vw;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  padding-left: 40px;
  padding-right: 40px;
  ${breakpoints.lg} {
    justify-content: space-between;
  }
`;

const IntroHeaderVerizonCallContainer = styled(FlexColumnContainer)`
  justify-content: space-evenly;
  align-self: center;
  flex-direction: column;
  position: relative;
  width: 100%;
  min-height: 280px;
  color: #000000;
  gap: 25px;
`;

const HeroSection = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  padding-top: 40px;
  ${breakpoints.mdMax} {
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
`;

const HeaderTitle = styled(Text)`
  line-height: 120%;
  ${breakpoints.md} {
    font-size: 2.5rem;
    line-height: 115%;
    max-width: 552px;
  }
`;

const HeaderBreaker = styled.span`
  display: block;
  ${breakpoints.md} {
    display: inline-block;
  }
`;

const HeaderSubTitle = styled(Text)`
  color: #6e767d;
  line-height: 130%;
  ${breakpoints.md} {
    font-size: 1rem;
    margin-bottom: 8px;
  }
`;

const TitleContainer = styled.div`
  margin-top: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
  flex-shrink: 2;
  justify-content: center;
  z-index: 1;
  gap: 1.5rem;
  ${breakpoints.md} {
    width: 50%;
  }
  ${breakpoints.mdMax} {
    align-items: center;
    text-align: center;
  }
`;

const AsurionButtonWrapper = styled(AsurionButton)`
  height: auto;
  max-height: 3rem;
  width: 212px;
`;

const SelfGuidedSetupWrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  z-index: 1;

  @media (${breakpoints.lgMin}) {
    justify-content: start;
    flex-direction: row;
  }
`;

const ImageContainer = styled(FlexColumnContainer)`
  height: 100%;
  justify-content: center;
  ${breakpoints.md} {
    width: 50%;
  }
`;

const SelfServiceImage = styled.img`
  height: 100%;
  object-fit: contain;
  max-height: 320px;
  left: 18px;
  position: relative;
  @media (${breakpoints.mdMin}) {
    max-height: 550px;
  }
`;

const ListItemsMobile = styled.div<{ showChatButton: boolean }>`
  max-width: 260px;
  margin: 0 auto;
  position: relative;
  ${({ showChatButton }) => (showChatButton ? "margin-bottom: 36px;" : "")}
  @media (${breakpoints.lgMin}) {
    display: none;
  }
  @media (${breakpoints.mdMin}) {
    max-width: 320px;
    top: -40px;
    margin-bottom: unset;
    margin-top: 25px;
  }
`;

const ListItemsDesktop = styled.div`
  ${breakpoints.mdMax} {
    display: none;
  }
`;

const ListItem = styled.div`
  margin-bottom: 6px;
  display: flex;
  align-items: center;
`;

const ListItemIcon = styled(Icon)`
  margin-right: 8px;
`;

const ListItemText = styled(Text)`
  color: #333f48;
`;

const FeedbackRatingContainer = styled(FlexColumnContainer)`
  align-items: center;
  position: relative;
  bottom: 50px;

  @media (${breakpoints.mdMin}) {
    bottom: 70px;
  }

  @media (${breakpoints.lgMin}) {
    left: 15px;
    bottom: 60px;
  }

  ${breakpoints.md2} {
    bottom: 80px;
  }
`;
const FeedbackSubTitle = styled(Text)``;
const FeedbackStarsImage = styled.img`
  width: 173.156px;
  height: 46.667px;
`;

// Just commented out the animation container of lottie player to be applied in the future.
// const AnimationContainer = styled(FlexColumnContainer)`
//   height: 100%;
//   justify-content: center;

//   ${breakpoints.lg} {
//     position: relative;
//     right: 5em;
//   }
// `;

const ListItems = () => {
  const t = useSanitizedTranslation();
  const texts = [
    "Live, reliable expert assistance",
    "500,000 setups every month",
    "Connect to an expert in as little as 1 min",
  ];
  return (
    <div>
      {texts.map(text => (
        <ListItem>
          <ListItemIcon src="Checkmark-V" size="2" />
          <ListItemText size={2}>{t(text)}</ListItemText>
        </ListItem>
      ))}
    </div>
  );
};

interface IntroHeaderVerizonCallProps {
  callButtonPhoneNumber?: string;
  showChatButton: boolean;
  pathfinderVersion: string;
  chatClient: typeof TwilioClient | typeof AEClient;
}

export const IntroHeaderVerizonCall: React.FC<IntroHeaderVerizonCallProps> = ({
  pathfinderVersion,
  showChatButton,
  chatClient,
}) => {
  const t = useSanitizedTranslation();
  const defaultSupportNumber = "866-314-7861";
  const [callNumber, setCallNumber] = useState(defaultSupportNumber);
  const supportNumber = defaultSupportNumber;

  useEffect(() => {
    if (
      typeof callNumber === "undefined" ||
      supportNumber !== defaultSupportNumber
    ) {
      setCallNumber(supportNumber);
    }
  }, [supportNumber]);

  const { width } = useWindowDimensions();
  const isDesktop = width >= Size.MD;

  const headerBreakerContent = showChatButton ? (
    <>
      The <strong>quickest</strong> way to <strong>activate and setup</strong>{" "}
      your phone.
    </>
  ) : (
    <>
      Call now to <strong>finish setting up your phone.</strong>
    </>
  );
  const shouldDisplayCallNumber =
    showChatButton && !isDesktop ? `Call: ${callNumber}` : callNumber;

  return (
    <Container>
      <ContentContainer>
        <IntroHeaderVerizonCallContainer>
          <HeroSection>
            <TitleContainer>
              <HeaderTitle size={5} weight="feather" forwardedAs="h1">
                <HeaderBreaker>{headerBreakerContent}</HeaderBreaker>
              </HeaderTitle>
              <HeaderSubTitle size={1} weight="feather" forwardedAs="p">
                {t(
                  showChatButton
                    ? "Connect with an expert now"
                    : "We'll have you on your way in no time",
                )}
              </HeaderSubTitle>
              <SelfGuidedSetupWrapper>
                {showChatButton ? (
                  <AsurionButtonWrapper
                    iconSrc="ChatBubbleCircle"
                    variant={isDesktop ? "default" : "outline"}
                    onClick={() => {
                      chatClient.openMessagingOverlay();
                      trackEvent(
                        `Pathfinder${pathfinderVersion}_Chat_Prompt_Click`,
                        {
                          buttonText: "Chat with an expert",
                        },
                      );
                    }}
                  >
                    Chat with an expert
                  </AsurionButtonWrapper>
                ) : null}
                <AsurionButtonWrapper
                  iconSrc="Phone"
                  variant={showChatButton && isDesktop ? "outline" : "default"}
                  onClick={() => {
                    window.open(`tel: ${callNumber}`, "_self");
                    trackEvent(
                      `Pathfinder${pathfinderVersion}_Call_Prompt_Click`,
                      {
                        buttonText: shouldDisplayCallNumber,
                      },
                    );
                  }}
                >
                  {shouldDisplayCallNumber}
                </AsurionButtonWrapper>
              </SelfGuidedSetupWrapper>
              <ListItemsDesktop>
                <ListItems />
              </ListItemsDesktop>
            </TitleContainer>
            
            {/* 
            
            Just commented out the lottie player to be applied in the future.

            <AnimationContainer>
              <LottiePlayer animationData={ChatBannerAnimationV4} loop={1} pauseAtFrame={1350} />
              <ListItemsMobile showChatButton={showChatButton}>
                <ListItems />
              </ListItemsMobile>
            </AnimationContainer> */}
            <ImageContainer>
              <SelfServiceImage src={PhoneWithIcons} />
              {showChatButton ? (
                <FeedbackRatingContainer>
                  <FeedbackStarsImage src={FeedbackStars} />
                  <FeedbackSubTitle
                    color={colors.neutralDeepest}
                    forwardedAs="p"
                  >
                    {t("4.8 average expert star-rating")}
                  </FeedbackSubTitle>
                </FeedbackRatingContainer>
              ) : null}
              <ListItemsMobile showChatButton={showChatButton}>
                <ListItems />
              </ListItemsMobile>
            </ImageContainer>
          </HeroSection>
        </IntroHeaderVerizonCallContainer>
      </ContentContainer>
    </Container>
  );
};
